import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useSettingsEditor, SaveBar } from "@/components/admin/useSettingsEditor";
import { useSettings } from "@/context/SettingsContext";
import { useEffect } from "react";

const FONT_OPTIONS = [
  "Playfair Display", "Fraunces", "Cormorant Garamond", "Bodoni Moda", "Spectral",
  "Merriweather", "EB Garamond", "Gloock", "Manrope", "Inter", "Work Sans",
  "Figtree", "Karla", "IBM Plex Sans", "Space Grotesk", "Chivo", "Nunito", "Poppins",
  "DM Sans", "Sora", "Plus Jakarta Sans", "Outfit",
];

const PRESETS = [
  { name: "Ambigara Default", primary: "#1E3A8A", secondary: "#F43F5E", accent: "#F59E0B", dark: "#0F172A", background: "#F8FAFC" },
  { name: "Emerald Serenity", primary: "#0F766E", secondary: "#F59E0B", accent: "#EF4444", dark: "#0F172A", background: "#F0FDF4" },
  { name: "Deep Plum", primary: "#5B21B6", secondary: "#EC4899", accent: "#F59E0B", dark: "#1E1B29", background: "#FAF5FF" },
  { name: "Sunset Coral", primary: "#DC2626", secondary: "#F59E0B", accent: "#7C3AED", dark: "#1F1717", background: "#FFF7ED" },
  { name: "Charcoal Elegance", primary: "#1F2937", secondary: "#F97316", accent: "#10B981", dark: "#0A0A0A", background: "#FAFAFA" },
];

function ColorField({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="text-slate-700 text-sm">{label}</Label>
      <div className="mt-1.5 flex items-center gap-2">
        <div className="relative">
          <input type="color" value={value || "#000000"} onChange={(e) => onChange(e.target.value)}
            className="w-12 h-11 rounded-xl border border-slate-200 cursor-pointer bg-white" data-testid={testid} />
        </div>
        <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="#RRGGBB"
          className="h-11 rounded-xl border-slate-200 font-mono text-sm" />
      </div>
    </div>
  );
}

export default function AdminTheme() {
  const { draft, patchPath, save, reset, saving } = useSettingsEditor();
  const { applyTheme } = useSettings();

  // Live preview: apply theme to CSS variables as user types
  useEffect(() => {
    if (draft?.theme) applyTheme(draft.theme);
  }, [draft?.theme, applyTheme]);

  if (!draft) return null;
  const t = draft.theme || {};

  const applyPreset = (p) => {
    ["primary", "secondary", "accent", "dark", "background"].forEach((k) => patchPath(`theme.${k}`, p[k]));
  };

  return (
    <div data-testid="cms-theme-page">
      <div className="mb-8">
        <p className="text-xs font-bold uppercase tracking-[0.2em] brand-secondary-text mb-2">Design</p>
        <h1 className="font-heading text-3xl sm:text-4xl text-slate-900">Theme & Fonts</h1>
        <p className="text-slate-500 mt-2">Changes preview live. Save when you&rsquo;re happy.</p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] p-6 sm:p-8 space-y-8">
        {/* Presets */}
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">Palette Presets</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {PRESETS.map((p) => (
              <button key={p.name} type="button" onClick={() => applyPreset(p)}
                data-testid={`preset-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-left rounded-2xl border border-slate-200 p-4 hover:border-slate-900 hover:shadow-md transition-all">
                <div className="flex gap-1.5 mb-3">
                  <span className="w-6 h-6 rounded-md" style={{ backgroundColor: p.primary }} />
                  <span className="w-6 h-6 rounded-md" style={{ backgroundColor: p.secondary }} />
                  <span className="w-6 h-6 rounded-md" style={{ backgroundColor: p.accent }} />
                  <span className="w-6 h-6 rounded-md border" style={{ backgroundColor: p.background }} />
                </div>
                <p className="text-sm font-medium text-slate-900">{p.name}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Colors */}
        <div className="border-t border-slate-100 pt-6">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-4">Colors</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <ColorField label="Primary (Navy)" value={t.primary} onChange={(v) => patchPath("theme.primary", v)} testid="color-primary" />
            <ColorField label="Secondary (Rose)" value={t.secondary} onChange={(v) => patchPath("theme.secondary", v)} testid="color-secondary" />
            <ColorField label="Accent (Gold)" value={t.accent} onChange={(v) => patchPath("theme.accent", v)} testid="color-accent" />
            <ColorField label="Dark Text" value={t.dark} onChange={(v) => patchPath("theme.dark", v)} />
            <ColorField label="Background" value={t.background} onChange={(v) => patchPath("theme.background", v)} />
          </div>
        </div>

        {/* Fonts */}
        <div className="border-t border-slate-100 pt-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <Label className="text-slate-700 text-sm">Heading Font</Label>
            <Select value={t.headingFont} onValueChange={(v) => patchPath("theme.headingFont", v)}>
              <SelectTrigger data-testid="font-heading-select" className="mt-1.5 h-11 rounded-xl border-slate-200">
                <SelectValue placeholder="Choose a heading font" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {FONT_OPTIONS.map((f) => (<SelectItem key={f} value={f} style={{ fontFamily: `'${f}'` }}>{f}</SelectItem>))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-400 mt-2" style={{ fontFamily: `'${t.headingFont}'` }}>Preview — The quick brown fox jumps over the lazy dog</p>
          </div>
          <div>
            <Label className="text-slate-700 text-sm">Body Font</Label>
            <Select value={t.bodyFont} onValueChange={(v) => patchPath("theme.bodyFont", v)}>
              <SelectTrigger data-testid="font-body-select" className="mt-1.5 h-11 rounded-xl border-slate-200">
                <SelectValue placeholder="Choose a body font" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {FONT_OPTIONS.map((f) => (<SelectItem key={f} value={f} style={{ fontFamily: `'${f}'` }}>{f}</SelectItem>))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-400 mt-2" style={{ fontFamily: `'${t.bodyFont}'` }}>Preview — The quick brown fox jumps over the lazy dog</p>
          </div>
        </div>

        {/* Live preview card */}
        <div className="border-t border-slate-100 pt-6">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">Live Preview</p>
          <div className="rounded-2xl p-6 border border-slate-100" style={{ backgroundColor: t.background }}>
            <h3 className="font-heading text-3xl" style={{ color: t.dark, fontFamily: `'${t.headingFont}'` }}>
              Shaping Future <span style={{ color: t.primary }}>Healthcare</span> Professionals
            </h3>
            <p className="mt-3 text-sm" style={{ color: t.dark, fontFamily: `'${t.bodyFont}'` }}>
              A quick preview of your typography and colors together. Buttons and highlights will use these accents.
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <span className="rounded-full px-5 py-2 text-white text-sm" style={{ backgroundColor: t.primary }}>Primary</span>
              <span className="rounded-full px-5 py-2 text-white text-sm" style={{ backgroundColor: t.secondary }}>Secondary</span>
              <span className="rounded-full px-5 py-2 text-white text-sm" style={{ backgroundColor: t.accent }}>Accent</span>
            </div>
          </div>
        </div>
      </div>

      <SaveBar onSave={save} onReset={reset} saving={saving} />
    </div>
  );
}
